import { Link } from 'react-router-dom';
import { DocsNav } from '../components/DocsNav';
import { Card, CommandBlock, Eyebrow, Heading, Lead, Section } from '../components/ui';
import { usePageTitle } from '../lib/usePageTitle';

function Code({ children }: { children: string }) {
  return (
    <pre className="mt-3 overflow-x-auto rounded-lg border border-hull-700 bg-hull-950 p-4 font-mono text-xs leading-relaxed text-hull-200">
      <code>{children}</code>
    </pre>
  );
}

export function Docs() {
  usePageTitle('Documentation — WaveQA');

  return (
    <>
      <DocsNav />

      <Section className="border-b border-hull-800">
        <Eyebrow>Documentation</Eyebrow>
        <Heading level={1} className="text-white">
          Getting started
        </Heading>
        <Lead className="mt-5 max-w-2xl">
          Enough to get from a fresh account to your first verification run.
        </Lead>
      </Section>

      <Section className="border-b border-hull-800" id="documents">
        <Heading className="text-white">The two documents you write</Heading>
        <Lead className="mt-3 max-w-2xl">
          These are the whole input to a run. WaveQA reads nothing else about your
          product, and will refuse to run against an unedited template.
        </Lead>

        <div className="mt-8 grid gap-4 md:grid-cols-2">
          {[
            {
              file: 'readme.md',
              where: 'project root — one per product',
              body: 'What the project is, how it is laid out and how to get it running. Its `## Environments` table is what `--env dev|staging|prod` reads. Any URL here becomes an origin the agent is allowed to reach — and the only way one gets into that set.',
            },
            {
              file: 'feature-spec.md',
              where: 'features/<name>/ — one per feature',
              body: 'Acceptance criteria and edge cases. Criteria are written as `- **AC-1**: <testable statement>`. Open questions become BLOCKED rows rather than guesses.',
            },
          ].map((doc) => (
            <Card key={doc.file}>
              <h3 className="font-mono text-sm font-semibold text-surge-300">{doc.file}</h3>
              <p className="mt-1 text-xs text-hull-500">{doc.where}</p>
              <p className="mt-2 text-sm leading-relaxed text-hull-300">{doc.body}</p>
            </Card>
          ))}
        </div>

        <Card className="mt-4">
          <h3 className="text-base font-semibold text-white">Run output</h3>
          <Code>{`WorkingFolder/
└── DemoProject/
    ├── project.json
    ├── readme.md
    └── features/
        └── Feature-01/
            ├── feature-spec.md
            └── Run-History/
                └── 2026-08-16_070129/
                    ├── waveqa-DemoProject-Feature-01-2026-08-16_070129.pdf
                    ├── waveqa-DemoProject-Feature-01-2026-08-16_070129.html
                    ├── acceptance-results.md
                    ├── acceptance-results.json
                    ├── execution-log.md
                    ├── metadata.json
                    └── evidence/`}</Code>
          <p className="mt-3 text-sm text-hull-300">
            The PDF is the one that gets attached to a ticket, so it is named
            for what it is about rather than{' '}
            <code className="text-surge-400">qa-report.pdf</code> &mdash; four of
            those in a downloads folder identify nothing. Open the newest with{' '}
            <code className="text-surge-400">waveqa report --open</code>.
          </p>
        </Card>
      </Section>

      <Section className="border-b border-hull-800" id="install">
        <Heading className="text-white">Install and configure</Heading>
        <Lead className="mt-3 max-w-2xl">
          WaveQA runs on Windows, Linux and macOS, and needs Python 3.11 or later.
        </Lead>

        <Card className="mt-6">
          <h3 className="text-base font-semibold text-white">1. Configure the agent</h3>
          <p className="mt-2 text-sm text-hull-300">
            Create a credential in your dashboard first, then run the wizard. The
            client secret is shown once, so copy it before closing the dialog.
          </p>
          <CommandBlock className="mt-3" label="Copy setup commands">{`waveqa setup

# or, for a browser interface
waveqa setup portal`}</CommandBlock>
        </Card>

        <Card className="mt-4">
          <h3 className="text-base font-semibold text-white">2. Check the environment</h3>
          <p className="mt-2 text-sm text-hull-300">
            Confirms credentials, working folder, credential storage backend, LLM
            configuration, browser runtime, API connectivity and live subscription
            state.
          </p>
          <CommandBlock className="mt-3" label="Copy doctor command">{`waveqa doctor`}</CommandBlock>
        </Card>

        <Card className="mt-4">
          <h3 className="text-base font-semibold text-white">3. Set up a feature</h3>
          <p className="mt-2 text-sm text-hull-300">
            Four steps, in order. You write two documents; WaveQA writes three.
            After the first <code className="text-surge-400">cd</code>,{' '}
            <code className="text-surge-400">-f</code> takes the project from the
            folder you are standing in.
          </p>
          <CommandBlock className="mt-3" label="Copy feature setup commands">{`waveqa -p "DemoProject"     # create the project, then write readme.md
cd DemoProject

waveqa -f "Feature-01"        # create the feature, then add feature-spec.md

waveqa -p "DemoProject" -f "Feature-01" --env dev

# staging and production come from the same table in your readme
waveqa -p "DemoProject" -f "Feature-01" --env staging`}</CommandBlock>
          <p className="mt-3 text-sm text-hull-300">
            <code className="text-surge-400">--env</code> takes{' '}
            <code className="text-surge-400">dev</code>,{' '}
            <code className="text-surge-400">staging</code> or{' '}
            <code className="text-surge-400">prod</code> (
            <code className="text-surge-400">--dev</code> and friends are
            shorthands), and reads that row from the{' '}
            <code className="font-mono text-surge-300">## Environments</code>{' '}
            table in your readme. Only that environment&rsquo;s URLs are
            reachable during the run, so a staging run cannot touch production.
            It defaults to <code className="text-surge-400">dev</code>.
          </p>
        </Card>

        <Card className="mt-4">
          <h3 className="text-base font-semibold text-white">
            4. See what is there
          </h3>
          <p className="mt-2 text-sm text-hull-300">
            Every one of these reads your working folder or the agent&rsquo;s own
            control repository &mdash; none of them contacts the server. Reports
            are written on your machine and stay there, so{' '}
            <code className="text-surge-400">waveqa report</code> is the only
            place that can list them.
          </p>
          <CommandBlock className="mt-3" label="Copy inspection commands">{`waveqa projects              # projects in your working folder
waveqa features              # features in this project, and which are ready

waveqa report                # past runs, newest first
waveqa report --open         # open the latest report

waveqa compliance frameworks         # what this build can verify
waveqa compliance controls owasp     # one framework's controls`}</CommandBlock>
          <p className="mt-3 text-sm text-hull-300">
            Every command, with its options and when to reach for it, is on the{' '}
            <Link className="text-surge-400 hover:text-surge-300" to="/docs/commands">
              command reference
            </Link>
            . <code className="text-surge-400">waveqa guide</code> prints the
            same thing in your terminal.
          </p>
        </Card>

        <Card className="mt-4">
          <h3 className="text-base font-semibold text-white">
            5. Keep it up to date
          </h3>
          <p className="mt-2 text-sm text-hull-300">
            Fetches the release manifest, checks its Ed25519 signature against the
            key built into your agent, verifies the download against the signed
            hash, and only then installs. It refuses an unsigned manifest, a
            modified artefact, and any release older than the one you have.
          </p>
          <CommandBlock className="mt-3" label="Copy update commands">{`waveqa update

# see what is available without installing
waveqa update --check`}</CommandBlock>
        </Card>

        <Card className="mt-4">
          <h3 className="text-base font-semibold text-white">
            6. Remove the agent
          </h3>
          <p className="mt-2 text-sm text-hull-300">
            Deletes the stored client secret, the LLM API key and the
            configuration directory. Your working folder is kept &mdash; it holds
            the specifications, evidence and reports you wrote, and nothing can
            regenerate them. Add <code className="text-surge-400">--purge-projects</code>{' '}
            to delete that too; it asks again before it does.
          </p>
          <CommandBlock className="mt-3" label="Copy uninstall commands">{`waveqa uninstall

# then remove the package itself
pip uninstall waveqa-agent`}</CommandBlock>
        </Card>
      </Section>

      <Section className="border-b border-hull-800" id="compliance">
        <Heading className="text-white">Security &amp; compliance verification</Heading>
        <Lead className="mt-3 max-w-2xl">
          Optional flags attach a framework&rsquo;s applicable controls to the same
          run, against the same pages, in the same report. With none of them, a
          run behaves exactly as it did before they existed.
        </Lead>

        <Card className="mt-6">
          <CommandBlock label="Copy security verification commands">{`# core web application security and headers
waveqa -p "DemoProject" -f "Feature-01" --dev --uwas --cwsh

# the applicable security bundle
waveqa -p "DemoProject" -f "Feature-01" --dev --security

# a healthcare feature
waveqa -p "HealthApp" -f "PATIENT-101" --dev --uwas --owasp --hipaa`}</CommandBlock>
          <p className="mt-4 text-sm leading-relaxed text-hull-300">
            Thirteen flags in all, covering OWASP ASVS, GDPR, CCPA/CPRA, HIPAA,
            PCI DSS, ISO/IEC 27001, WCAG and more. A framework that is irrelevant
            to your application is reported{' '}
            <code className="text-surge-400">NOT_APPLICABLE</code> with the reason
            rather than executed, and a control automation cannot settle is
            reported <code className="text-surge-400">EVIDENCE_REQUIRED</code>{' '}
            rather than passed.
          </p>
          <p className="mt-4 text-sm">
            <Link
              to="/docs/security-compliance"
              className="font-medium text-surge-400 underline hover:text-surge-300"
            >
              Read the Security &amp; Compliance reference &rarr;
            </Link>
          </p>
        </Card>
      </Section>

      <Section className="border-b border-hull-800" id="verdicts">
        <Heading className="text-white">Reading a verdict</Heading>
        <Lead className="mt-3 max-w-2xl">
          Five statuses, deliberately not two. The distinction between &ldquo;this
          is broken&rdquo; and &ldquo;this was not checked&rdquo; is the whole
          point.
        </Lead>

        <ul className="mt-8 space-y-3">
          {[
            ['PASS', 'text-pass-400', 'Verified, with at least one stored artefact cited. A PASS without evidence is rejected before it can be written.'],
            ['FAIL', 'text-fail-400', 'The check ran and the application did not satisfy the criterion.'],
            ['BLOCKED', 'text-warn-400', 'The criterion could not be settled, and the row says why — the environment was unreachable, no automated check could be derived from the wording, the checks captured nothing to substantiate a pass, or the review did not resolve. Not evidence of a defect.'],
          ].map(([status, tone, body]) => (
            <li key={status} className="flex flex-col gap-1 border-l-2 border-hull-700 pl-4 sm:flex-row sm:gap-4">
              <span className={`w-32 shrink-0 font-mono text-sm font-semibold ${tone}`}>
                {status}
              </span>
              <span className="text-sm text-hull-300">{body}</span>
            </li>
          ))}
        </ul>

        <p className="mt-6 max-w-2xl text-sm text-hull-400">
          A run containing any FAIL is FAILED. A run with unverified criteria is
          INCOMPLETE — never PASSED. Reporting green because nothing actively
          failed would be the most misleading thing a QA tool could do.
        </p>
      </Section>

      <Section id="security">
        <Heading className="text-white">Security notes</Heading>

        <div className="mt-8 grid gap-4 md:grid-cols-2">
          {[
            {
              title: 'Where secrets live',
              body: 'Client secrets and LLM API keys go to Windows Credential Manager, macOS Keychain or Linux Secret Service. If no keystore is available, WaveQA falls back to an AES-256-GCM file bound to the machine and tells you so in `waveqa doctor` — it does not pretend the fallback is equivalent.',
            },
            {
              title: 'Command execution',
              body: 'Proposed commands are checked against a denylist of destructive patterns and an allowlist of known programs. Command chaining is refused outright, and anything unrecognised is denied by default.',
            },
            {
              title: 'Untrusted content',
              body: 'Pages, API responses and project files are fenced as data before reaching a model. Instructions found inside them are reported as findings, never obeyed.',
            },
            {
              title: 'Evidence redaction',
              body: 'Authorization headers, cookies, API keys, bearer tokens and credentials embedded in URLs are stripped before anything is written to a run folder.',
            },
          ].map((note) => (
            <Card key={note.title}>
              <h3 className="text-base font-semibold text-white">{note.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-hull-300">{note.body}</p>
            </Card>
          ))}
        </div>
      </Section>
    </>
  );
}
